import { EffectShape } from '../types/enums';
import type { DirectionEnum } from '../types/enums';
import type { IEffectShapeConfig } from '../types/entity.types';
import type { IPosition } from '../types/common.types';
import {
  getTargetPosition,
  getSwipeDirections,
  getConeCells,
} from './map.utils';

interface ShapeResolveParams {
  /** Center for target-centered shapes (SINGLE, CIRCLE, CROSS, CONE). */
  targetX?: number;
  targetY?: number;
  /** Origin for caster-centered shapes (SWIPE, LINE). */
  casterX: number;
  casterY: number;
  direction: DirectionEnum;
  /** Fallback range when a shape omits its own. */
  defaultRange?: number;
}

/** Resolves affected cells for a set of shapes. Pure (no entity/wall lookups). */
export function resolveShapeCells(
  shapes: IEffectShapeConfig[] | undefined,
  p: ShapeResolveParams,
): IPosition[] {
  const cells: IPosition[] = [];
  if (!shapes) {
    return cells;
  }
  const { targetX, targetY, casterX, casterY, direction } = p;
  for (const shape of shapes) {
    switch (shape.type) {
      case EffectShape.SINGLE:
        if (targetX !== undefined && targetY !== undefined) {
          cells.push({ x: targetX, y: targetY });
        }
        break;

      case EffectShape.SWIPE: {
        const swipeDirs = getSwipeDirections(direction);
        for (const swipeDir of swipeDirs) {
          const pos = getTargetPosition(casterX, casterY, swipeDir);
          cells.push({ x: pos.x, y: pos.y });
        }
        break;
      }

      case EffectShape.CIRCLE: {
        const r = shape.range ?? 1;
        const cx = targetX ?? casterX;
        const cy = targetY ?? casterY;
        for (let dx = -r; dx <= r; dx++) {
          for (let dy = -r; dy <= r; dy++) {
            if (shape.excludeCenter && dx === 0 && dy === 0) {
              continue;
            }
            if (dx * dx + dy * dy <= r * r) {
              cells.push({ x: cx + dx, y: cy + dy });
            }
          }
        }
        break;
      }

      case EffectShape.CROSS: {
        if (targetX !== undefined && targetY !== undefined) {
          cells.push({ x: targetX, y: targetY });
          cells.push({ x: targetX + 1, y: targetY });
          cells.push({ x: targetX - 1, y: targetY });
          cells.push({ x: targetX, y: targetY + 1 });
          cells.push({ x: targetX, y: targetY - 1 });
        }
        break;
      }

      case EffectShape.CONE: {
        if (targetX !== undefined && targetY !== undefined) {
          const coneRange = shape.range ?? 3;
          const { allCells } = getConeCells(
            targetX,
            targetY,
            direction,
            coneRange,
            true,
          );
          cells.push(...allCells);
        }
        break;
      }

      case EffectShape.LINE: {
        const lr = shape.range ?? p.defaultRange ?? 1;
        let cx = casterX;
        let cy = casterY;
        for (let i = 0; i < lr; i++) {
          const next = getTargetPosition(cx, cy, direction);
          cells.push({ x: next.x, y: next.y });
          cx = next.x;
          cy = next.y;
        }
        break;
      }

      default:
        break;
    }
  }
  return cells;
}
